import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Send, MessageSquare, Paperclip, X, FileText, Download } from "lucide-react";
import { useMyWorkerRecord, useMyMessages, useSendMessage } from "@/hooks/useWorkerPortal";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { toast } from "sonner";

interface WorkerAttachment {
  name: string;
  path: string;
  size: number;
  type: string;
  url: string;
}

const isImage = (type: string) => type.startsWith("image/");
const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const WorkerMessages = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: worker, isLoading: wl } = useMyWorkerRecord();
  const { data: messages = [], isLoading: ml } = useMyMessages(worker?.id);
  const sendMutation = useSendMessage();
  const [message, setMessage] = useState("");
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Real-time subscription
  useEffect(() => {
    if (!worker?.id) return;
    const channel = supabase
      .channel(`messages-${worker.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "personnel_messages",
          filter: `worker_id=eq.${worker.id}`,
        },
        () => {
          qc.invalidateQueries({ queryKey: ["my_messages", worker.id] });
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [worker?.id, qc]);

  // Auto-scroll
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length]);

  const uploadFile = async (file: File): Promise<WorkerAttachment> => {
    const fileExt = file.name.split(".").pop();
    const uniqueName = `${crypto.randomUUID()}.${fileExt}`;
    const path = `worker-${worker?.id}/${uniqueName}`;
    const { error } = await supabase.storage.from("chat-attachments").upload(path, file, { upsert: false });
    if (error) throw error;
    const { data: urlData } = supabase.storage.from("chat-attachments").getPublicUrl(path);
    return { name: file.name, path, size: file.size, type: file.type, url: urlData.publicUrl };
  };

  const handleSend = async () => {
    if (!message.trim() && pendingFiles.length === 0) return;
    if (!worker || !user) return;

    let attachments: WorkerAttachment[] = [];
    if (pendingFiles.length > 0) {
      setUploading(true);
      try {
        attachments = await Promise.all(pendingFiles.map(uploadFile));
      } catch (err: any) {
        toast.error(err.message || "Failed to upload attachment");
        setUploading(false);
        return;
      }
      setUploading(false);
    }

    try {
      await sendMutation.mutateAsync({
        company_id: worker.company_id,
        worker_id: worker.id,
        personnel_project_id: worker.personnel_project_id,
        message: message.trim() || (attachments.length > 0 ? `📎 ${attachments.map(a => a.name).join(", ")}` : ""),
        sent_by: user.id,
        sender_type: "worker",
        sender_name: worker.worker_name || `${worker.first_name || ""} ${worker.last_name || ""}`.trim(),
      });
      setMessage("");
      setPendingFiles([]);
    } catch (err: any) {
      toast.error(err.message || "Failed to send");
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    const valid = files.filter(f => f.size <= 20 * 1024 * 1024).slice(0, 5);
    if (valid.length < files.length) toast.error("Some files were skipped (max 20MB, 5 files)");
    setPendingFiles(prev => [...prev, ...valid]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removePendingFile = (idx: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== idx));
  };

  if (wl || ml) return <div className="space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-[400px]" /></div>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Messages</h1>

      <Card className="flex flex-col h-[500px]">
        <CardHeader><CardTitle className="text-base">Project Messages</CardTitle></CardHeader>
        <CardContent className="flex-1 flex flex-col min-h-0">
          <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-3">
            {messages.length === 0 ? (
              <div className="flex-1 flex items-center justify-center h-full text-muted-foreground">
                <div className="text-center">
                  <MessageSquare className="h-10 w-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No messages yet</p>
                  <p className="text-xs">Messages with your project manager will appear here</p>
                </div>
              </div>
            ) : (
              messages.map((m: any) => {
                const isMe = m.sender_type === "worker";
                return (
                  <div key={m.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-lg px-3 py-2 ${isMe ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                      {!isMe && <p className="text-xs font-medium mb-0.5 opacity-70">{m.sender_name}</p>}
                      <p className="text-sm">{m.message}</p>
                      <p className={`text-[10px] mt-0.5 ${isMe ? "opacity-70" : "text-muted-foreground"}`}>
                        {m.created_at ? format(new Date(m.created_at), "MMM d, h:mm a") : ""}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Pending attachments preview */}
          {pendingFiles.length > 0 && (
            <div className="border-t pt-2 mt-2 flex gap-2 flex-wrap">
              {pendingFiles.map((file, i) => (
                <div key={i} className="relative group flex items-center gap-1.5 bg-muted border rounded-lg px-2 py-1.5 text-xs">
                  {file.type.startsWith("image/") ? (
                    <img src={URL.createObjectURL(file)} alt={file.name} className="h-10 w-10 rounded object-cover" />
                  ) : (
                    <FileText className="h-5 w-5 text-muted-foreground" />
                  )}
                  <div className="max-w-[80px]">
                    <p className="truncate font-medium">{file.name}</p>
                    <p className="text-muted-foreground">{formatFileSize(file.size)}</p>
                  </div>
                  <button
                    onClick={() => removePendingFile(i)}
                    className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2 mt-4 pt-3 border-t">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip"
              onChange={handleFileSelect}
            />
            <Button size="icon" variant="ghost" onClick={() => fileInputRef.current?.click()} type="button">
              <Paperclip className="h-4 w-4" />
            </Button>
            <Input
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type a message..."
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSend()}
            />
            <Button
              size="icon"
              disabled={(!message.trim() && pendingFiles.length === 0) || sendMutation.isPending || uploading}
              onClick={handleSend}
            >
              {uploading ? (
                <div className="animate-spin h-4 w-4 border-2 border-primary-foreground border-t-transparent rounded-full" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WorkerMessages;
